import React, { useState, useEffect } from 'react';
import { Post, Tag } from '../../types/auth';
import authService from '../../services/authService';
import './PostModal.css';

interface PostModalProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (postData: Post) => void;
}

const PostModal: React.FC<PostModalProps> = ({
  post,
  isOpen,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<Partial<Post>>({
    attributes: { title: '' },
    text: '',
    script: '',
    first_question: '',
    image_url: '',
    link_url: '',
    thumbnail: '',
    video_url: '',
    youtube_url: '',
    audio_url: '',
    lrc_url: '',
    post_language: 'hi',
    post_type: '',
    post_title: '',
    post_subtitle: '',
    post_order: 1,
    tags: [],
    category: '',
  });

  // MCQSC specific state
  const [mcqscData, setMcqscData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswer: '',
    explanation: '',
  });

  // MCQMC specific state
  const [mcqmcData, setMcqmcData] = useState({
    question: '',
    optionA: '',
    optionB: '',
    optionC: '',
    optionD: '',
    correctAnswers: {
      A: false,
      B: false,
      C: false,
      D: false,
    },
    explanations: {
      A: '',
      B: '',
      C: '',
      D: '',
    },
  });

  // QNA specific state
  const [qnaData, setQnaData] = useState({
    question: '',
    answer: '',
    explanation: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  const categoryMap: Record<string, string> = {
    devotional: 'Devotional',
    'self-help': 'Self Help',
    health: 'Health',
  };

  const categories = Object.keys(categoryMap);

  const fetchTags = async () => {
    try {
      setLoadingTags(true);
      const response = await authService.getTags();
      setAvailableTags(response.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  // Reset form data when modal opens for a new post
  const resetFormData = () => {
    setFormData({
      attributes: { title: '' },
      text: '',
      script: '',
      first_question: '',
      image_url: '',
      link_url: '',
      thumbnail: '',
      video_url: '',
      youtube_url: '',
      audio_url: '',
      lrc_url: '',
      post_language: 'hi',
      post_type: '',
      post_title: '',
      post_subtitle: '',
      post_order: 1,
      tags: [],
      category: '',
    });
    setMcqscData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswer: '',
      explanation: '',
    });
    setMcqmcData({
      question: '',
      optionA: '',
      optionB: '',
      optionC: '',
      optionD: '',
      correctAnswers: {
        A: false,
        B: false,
        C: false,
        D: false,
      },
      explanations: {
        A: '',
        B: '',
        C: '',
        D: '',
      },
    });
    setQnaData({
      question: '',
      answer: '',
      explanation: '',
    });
    setErrors({});
  };

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
    const fetchPostData = async () => {
      if (post) {
        setLoading(true);
        try {
          // Call getPostMeta API to get the complete post details
          const postMetaData = await authService.getPostMeta(post.id);
          console.log('Post meta data received:', postMetaData);

          const categoryValue = (postMetaData.category || '').trim();
          console.log('PostModal - Setting category from API:', categoryValue);

          setFormData({
            attributes: postMetaData.attributes || { title: '' },
            text: postMetaData.text || '',
            script: postMetaData.script || '',
            first_question: postMetaData.first_question || '',
            image_url: postMetaData.image_url || '',
            link_url: postMetaData.link_url || '',
            thumbnail: postMetaData.thumbnail || '',
            video_url: postMetaData.video_url || '',
            youtube_url: postMetaData.youtube_url || '',
            audio_url: postMetaData.audio_url || '',
            lrc_url: postMetaData.lrc_url || '',
            post_language: postMetaData.post_language || '',
            post_type: postMetaData.post_type || '',
            post_title: postMetaData.post_title || '',
            post_subtitle: postMetaData.post_subtitle || '',
            post_order: postMetaData.post_order || 1,
            tags: postMetaData.tags || [],
            category: categoryValue,
          });

          // Populate MCQSC data if post type is mcqsc
          if (postMetaData.post_type === 'mcqsc') {
            // Check both root level and attributes for options and answers
            const options =
              postMetaData.options || postMetaData.attributes?.options || [];
            const answers =
              postMetaData.answers || postMetaData.attributes?.answers || [];

            console.log('MCQSC options found:', options);
            console.log('MCQSC answers found:', answers);

            setMcqscData({
              question:
                postMetaData.first_question || postMetaData.question || '',
              optionA:
                options.find((opt: any) => opt.option === 'A')?.text || '',
              optionB:
                options.find((opt: any) => opt.option === 'B')?.text || '',
              optionC:
                options.find((opt: any) => opt.option === 'C')?.text || '',
              optionD:
                options.find((opt: any) => opt.option === 'D')?.text || '',
              correctAnswer: answers[0]?.correct_answer || '',
              explanation: answers[0]?.explanation || '',
            });
          }

          // Populate MCQMC data if post type is mcqmc
          if (postMetaData.post_type === 'mcqmc') {
            // Check both root level and attributes for options and answers
            const options =
              postMetaData.options || postMetaData.attributes?.options || [];
            const answers =
              postMetaData.answers || postMetaData.attributes?.answers || [];

            console.log('MCQMC options found:', options);
            console.log('MCQMC answers found:', answers);

            // Build correct answers object from answers array
            const correctAnswers = {
              A: false,
              B: false,
              C: false,
              D: false,
            };
            // Build explanations object from answers array
            const explanations = {
              A: '',
              B: '',
              C: '',
              D: '',
            };

            answers.forEach((answer: any) => {
              if (answer.correct_answer) {
                correctAnswers[
                  answer.correct_answer as keyof typeof correctAnswers
                ] = true;
                explanations[
                  answer.correct_answer as keyof typeof explanations
                ] = answer.explanation || '';
              }
            });

            setMcqmcData({
              question:
                postMetaData.first_question || postMetaData.question || '',
              optionA:
                options.find((opt: any) => opt.option === 'A')?.text || '',
              optionB:
                options.find((opt: any) => opt.option === 'B')?.text || '',
              optionC:
                options.find((opt: any) => opt.option === 'C')?.text || '',
              optionD:
                options.find((opt: any) => opt.option === 'D')?.text || '',
              correctAnswers,
              explanations,
            });
          }

          // Populate QNA data if post type is qna
          if (postMetaData.post_type === 'qna') {
            // Check both root level and attributes for answers
            const answers =
              postMetaData.answers || postMetaData.attributes?.answers || [];

            console.log('QNA answers found:', answers);

            setQnaData({
              question:
                postMetaData.first_question || postMetaData.question || '',
              answer: answers[0]?.correct_answer || answers[0]?.answer || '',
              explanation: answers[0]?.explanation || '',
            });
          }
        } catch (error) {
          console.error('Error fetching post metadata:', error);
          // Fallback to original post data if API call fails
          const fallbackCategoryValue = (post.category || '').trim();
          console.log(
            'PostModal - Setting category from fallback:',
            fallbackCategoryValue
          );

          setFormData({
            attributes: post.attributes || { title: '' },
            text: post.text || '',
            script: post.script || '',
            first_question: post.first_question || '',
            image_url: post.image_url || '',
            link_url: post.link_url || '',
            thumbnail: post.thumbnail || '',
            video_url: post.video_url || '',
            youtube_url: post.youtube_url || '',
            audio_url: post.audio_url || '',
            lrc_url: post.lrc_url || '',
            post_language: post.post_language || '',
            post_type: post.post_type || '',
            post_title: post.post_title || '',
            post_subtitle: post.post_subtitle || '',
            post_order: post.post_order || 1,
            tags: post.tags || [],
            category: fallbackCategoryValue,
          });

          // Populate MCQSC data from fallback if post type is mcqsc
          if (post.post_type === 'mcqsc') {
            // Check both root level and attributes for options and answers in fallback
            const options = post.options || post.attributes?.options || [];
            const answers = post.answers || post.attributes?.answers || [];

            console.log('MCQSC fallback options found:', options);
            console.log('MCQSC fallback answers found:', answers);

            setMcqscData({
              question: post.first_question || post.question || '',
              optionA:
                options.find((opt: any) => opt.option === 'A')?.text || '',
              optionB:
                options.find((opt: any) => opt.option === 'B')?.text || '',
              optionC:
                options.find((opt: any) => opt.option === 'C')?.text || '',
              optionD:
                options.find((opt: any) => opt.option === 'D')?.text || '',
              correctAnswer: answers[0]?.correct_answer || '',
              explanation: answers[0]?.explanation || '',
            });
          }

          // Populate MCQMC data from fallback if post type is mcqmc
          if (post.post_type === 'mcqmc') {
            // Check both root level and attributes for options and answers in fallback
            const options = post.options || post.attributes?.options || [];
            const answers = post.answers || post.attributes?.answers || [];

            console.log('MCQMC fallback options found:', options);
            console.log('MCQMC fallback answers found:', answers);

            // Build correct answers object from answers array
            const correctAnswers = {
              A: false,
              B: false,
              C: false,
              D: false,
            };
            // Build explanations object from answers array
            const explanations = {
              A: '',
              B: '',
              C: '',
              D: '',
            };

            answers.forEach((answer: any) => {
              if (answer.correct_answer) {
                correctAnswers[
                  answer.correct_answer as keyof typeof correctAnswers
                ] = true;
                explanations[
                  answer.correct_answer as keyof typeof explanations
                ] = answer.explanation || '';
              }
            });

            setMcqmcData({
              question: post.first_question || post.question || '',
              optionA:
                options.find((opt: any) => opt.option === 'A')?.text || '',
              optionB:
                options.find((opt: any) => opt.option === 'B')?.text || '',
              optionC:
                options.find((opt: any) => opt.option === 'C')?.text || '',
              optionD:
                options.find((opt: any) => opt.option === 'D')?.text || '',
              correctAnswers,
              explanations,
            });
          }

          // Populate QNA data from fallback if post type is qna
          if (post.post_type === 'qna') {
            // Check both root level and attributes for answers in fallback
            const answers = post.answers || post.attributes?.answers || [];

            console.log('QNA fallback answers found:', answers);

            setQnaData({
              question: post.first_question || post.question || '',
              answer: answers[0]?.correct_answer || answers[0]?.answer || '',
              explanation: answers[0]?.explanation || '',
            });
          }
        } finally {
          setLoading(false);
        }
      } else {
        // Reset form for new post creation
        resetFormData();
      }
    };

    fetchPostData();
  }, [post, isOpen]);

  // Reset form when modal is opened for new post creation
  useEffect(() => {
    if (isOpen && !post) {
      resetFormData();
    }
  }, [isOpen, post]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.attributes?.title?.trim())
      newErrors.title = 'Title is required';
    if (!formData.post_type?.trim())
      newErrors.post_type = 'Post type is required';
    if (!formData.post_order || formData.post_order <= 0)
      newErrors.post_order = 'Post order must be greater than 0';

    // MCQSC specific validation
    if (formData.post_type === 'mcqsc') {
      if (!mcqscData.question.trim())
        newErrors.mcqsc_question = 'Question is required for MCQSC';
      if (!mcqscData.optionA.trim())
        newErrors.mcqsc_optionA = 'Option A is required';
      if (!mcqscData.optionB.trim())
        newErrors.mcqsc_optionB = 'Option B is required';
      if (!mcqscData.optionC.trim())
        newErrors.mcqsc_optionC = 'Option C is required';
      if (!mcqscData.optionD.trim())
        newErrors.mcqsc_optionD = 'Option D is required';
      if (!mcqscData.correctAnswer.trim())
        newErrors.mcqsc_correctAnswer = 'Correct answer is required';
      if (!mcqscData.explanation.trim())
        newErrors.mcqsc_explanation = 'Explanation is required';
    }

    // Poll specific validation (same as MCQSC but no correct answer required)
    if (formData.post_type === 'poll') {
      if (!mcqscData.question.trim())
        newErrors.mcqsc_question = 'Question is required for Poll';
      if (!mcqscData.optionA.trim())
        newErrors.mcqsc_optionA = 'Option A is required';
      if (!mcqscData.optionB.trim())
        newErrors.mcqsc_optionB = 'Option B is required';
      if (!mcqscData.optionC.trim())
        newErrors.mcqsc_optionC = 'Option C is required';
      if (!mcqscData.optionD.trim())
        newErrors.mcqsc_optionD = 'Option D is required';
    }

    // MCQMC specific validation
    if (formData.post_type === 'mcqmc') {
      if (!mcqmcData.question.trim())
        newErrors.mcqmc_question = 'Question is required for MCQMC';
      if (!mcqmcData.optionA.trim())
        newErrors.mcqmc_optionA = 'Option A is required';
      if (!mcqmcData.optionB.trim())
        newErrors.mcqmc_optionB = 'Option B is required';
      if (!mcqmcData.optionC.trim())
        newErrors.mcqmc_optionC = 'Option C is required';
      if (!mcqmcData.optionD.trim())
        newErrors.mcqmc_optionD = 'Option D is required';

      // Check if at least one answer is selected
      const hasSelectedAnswer = Object.values(mcqmcData.correctAnswers).some(
        selected => selected
      );
      if (!hasSelectedAnswer)
        newErrors.mcqmc_correctAnswers =
          'At least one correct answer must be selected';

      // Validate explanations for selected correct answers
      Object.entries(mcqmcData.correctAnswers).forEach(
        ([option, isSelected]) => {
          if (
            isSelected &&
            !mcqmcData.explanations[
              option as keyof typeof mcqmcData.explanations
            ].trim()
          ) {
            newErrors[`mcqmc_explanation_${option}`] =
              `Explanation for option ${option} is required`;
          }
        }
      );
    }

    // QNA specific validation
    if (formData.post_type === 'qna') {
      if (!qnaData.question.trim())
        newErrors.qna_question = 'Question is required for QNA';
      if (!qnaData.answer.trim())
        newErrors.qna_answer = 'Answer is required for QNA';
      if (!qnaData.explanation.trim())
        newErrors.qna_explanation = 'Explanation is required for QNA';
    }

    // Audio specific validation
    if (formData.post_type === 'audio') {
      if (!formData.audio_url?.trim())
        newErrors.audio_url = 'Audio URL is required for audio posts';
      if (!formData.lrc_url?.trim())
        newErrors.lrc_url = 'LRC URL is required for audio posts';
    }

    // Optional audio URL validation for all post types
    if (formData.audio_url?.trim()) {
      try {
        new URL(formData.audio_url);
      } catch {
        newErrors.audio_url = 'Please enter a valid audio URL';
      }
    }

    // Optional LRC URL validation when audio URL is provided
    if (formData.audio_url?.trim() && formData.lrc_url?.trim()) {
      try {
        new URL(formData.lrc_url);
      } catch {
        newErrors.lrc_url = 'Please enter a valid LRC URL';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      let postDataToSave = { ...formData };

      // Format MCQSC data for API
      if (formData.post_type === 'mcqsc') {
        postDataToSave = {
          ...formData,
          question: mcqscData.question,
          // Only include first_question if user explicitly entered something
          ...(formData.first_question?.trim() && {
            first_question: formData.first_question,
          }),
          // Format options as required by API at root level
          options: [
            { text: mcqscData.optionA, option: 'A' },
            { text: mcqscData.optionB, option: 'B' },
            { text: mcqscData.optionC, option: 'C' },
            { text: mcqscData.optionD, option: 'D' },
          ],
          answers: [
            {
              explanation: mcqscData.explanation,
              correct_answer: mcqscData.correctAnswer,
            },
          ],
          attributes: {
            title: formData.attributes?.title || '',
            ...formData.attributes,
          },
        };
      }

      // Format Poll data for API (same as MCQSC but no answers)
      if (formData.post_type === 'poll') {
        postDataToSave = {
          ...formData,
          question: mcqscData.question,
          // Only include first_question if user explicitly entered something
          ...(formData.first_question?.trim() && {
            first_question: formData.first_question,
          }),
          // Format options as required by API at root level
          options: [
            { text: mcqscData.optionA, option: 'A' },
            { text: mcqscData.optionB, option: 'B' },
            { text: mcqscData.optionC, option: 'C' },
            { text: mcqscData.optionD, option: 'D' },
          ],
          answers: [],
          attributes: {
            title: formData.attributes?.title || '',
            ...formData.attributes,
          },
        };
      }

      // Format MCQMC data for API
      if (formData.post_type === 'mcqmc') {
        // Create separate answer objects for each selected correct answer with individual explanations
        const selectedAnswers = Object.entries(mcqmcData.correctAnswers)
          .filter(([, isSelected]) => isSelected)
          .map(([option]) => ({
            explanation:
              mcqmcData.explanations[
                option as keyof typeof mcqmcData.explanations
              ],
            correct_answer: option,
          }));

        postDataToSave = {
          ...formData,
          question: mcqmcData.question,
          // Only include first_question if user explicitly entered something
          ...(formData.first_question?.trim() && {
            first_question: formData.first_question,
          }),
          // Format options as required by API at root level
          options: [
            { text: mcqmcData.optionA, option: 'A' },
            { text: mcqmcData.optionB, option: 'B' },
            { text: mcqmcData.optionC, option: 'C' },
            { text: mcqmcData.optionD, option: 'D' },
          ],
          answers: selectedAnswers,
          attributes: {
            title: formData.attributes?.title || '',
            ...formData.attributes,
          },
        };
      }

      // Format QNA data for API
      if (formData.post_type === 'qna') {
        postDataToSave = {
          ...formData,
          question: qnaData.question,
          // Only include first_question if user explicitly entered something
          ...(formData.first_question?.trim() && {
            first_question: formData.first_question,
          }),
          // QNA has no options, set to null as specified
          options: null,
          answers: [
            {
              correct_answer: qnaData.answer,
              explanation: qnaData.explanation,
            },
          ],
          attributes: {
            title: formData.attributes?.title || '',
            ...formData.attributes,
          },
        };
      }

      onSave(postDataToSave as Post);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        attributes: { ...prev.attributes, title: value },
      }));
    } else if (name === 'post_order') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleMcqscInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setMcqscData(prev => ({ ...prev, [name]: value }));
    if (errors[`mcqsc_${name}`]) {
      setErrors(prev => ({ ...prev, [`mcqsc_${name}`]: '' }));
    }
  };

  const handleMcqmcInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setMcqmcData(prev => ({ ...prev, [name]: value }));
    if (errors[`mcqmc_${name}`]) {
      setErrors(prev => ({ ...prev, [`mcqmc_${name}`]: '' }));
    }
  };

  const handleMcqmcCorrectAnswerChange = (option: string) => {
    setMcqmcData(prev => ({
      ...prev,
      correctAnswers: {
        ...prev.correctAnswers,
        [option]:
          !prev.correctAnswers[option as keyof typeof prev.correctAnswers],
      },
    }));
    if (errors.mcqmc_correctAnswers) {
      setErrors(prev => ({ ...prev, mcqmc_correctAnswers: '' }));
    }
  };

  const handleMcqmcExplanationChange = (option: string, value: string) => {
    setMcqmcData(prev => ({
      ...prev,
      explanations: {
        ...prev.explanations,
        [option]: value,
      },
    }));
    if (errors[`mcqmc_explanation_${option}`]) {
      setErrors(prev => ({ ...prev, [`mcqmc_explanation_${option}`]: '' }));
    }
  };

  const handleQnaInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setQnaData(prev => ({ ...prev, [name]: value }));
    if (errors[`qna_${name}`]) {
      setErrors(prev => ({ ...prev, [`qna_${name}`]: '' }));
    }
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIds = Array.from(
      e.target.selectedOptions,
      option => option.value
    );
    const selectedSlugs = availableTags
      .filter(tag => selectedIds.includes(tag.id))
      .map(tag => tag.slug);
    setFormData(prev => ({ ...prev, tags: selectedSlugs }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {post ? `Edit Post (ID: ${post.id})` : 'Create Post'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {loading ? (
          <div
            className="loading-container"
            style={{ padding: '2rem', textAlign: 'center' }}
          >
            <div style={{ marginBottom: '1rem' }}>Loading post details...</div>
            <div
              className="loading-spinner"
              style={{
                border: '3px solid #f3f3f3',
                borderRadius: '50%',
                borderTop: '3px solid #007bff',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto',
              }}
            ></div>
          </div>
        ) : (
          <form className="modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="title" className="form-label">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.attributes?.title || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.title ? 'error' : ''}`}
                placeholder="Enter post title"
              />
              {errors.title && (
                <span className="error-message">{errors.title}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="post_title" className="form-label">
                Post Title
              </label>
              <input
                type="text"
                id="post_title"
                name="post_title"
                value={formData.post_title || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.post_title ? 'error' : ''}`}
                placeholder="Enter post title"
              />
              {errors.post_title && (
                <span className="error-message">{errors.post_title}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="post_subtitle" className="form-label">
                Post Subtitle
              </label>
              <input
                type="text"
                id="post_subtitle"
                name="post_subtitle"
                value={formData.post_subtitle || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.post_subtitle ? 'error' : ''}`}
                placeholder="Enter post subtitle"
              />
              {errors.post_subtitle && (
                <span className="error-message">{errors.post_subtitle}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="post_type" className="form-label">
                Post Type <span className="required">*</span>
              </label>
              <select
                id="post_type"
                name="post_type"
                value={formData.post_type || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.post_type ? 'error' : ''}`}
              >
                <option value="">Select post type</option>
                <option value="video">Video</option>
                <option value="audio">Audio</option>
                <option value="html">HTML</option>
                <option value="image">Image</option>
                <option value="mcqsc">MCQSC</option>
                <option value="mcqmc">MCQMC</option>
                <option value="qna">QNA</option>
                <option value="poll">Poll</option>
              </select>
              {errors.post_type && (
                <span className="error-message">{errors.post_type}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="text" className="form-label">
                Text
              </label>
              <textarea
                id="text"
                name="text"
                value={formData.text || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.text ? 'error' : ''}`}
                placeholder="Enter post text"
                rows={4}
              />
              {errors.text && (
                <span className="error-message">{errors.text}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="image_url" className="form-label">
                Image URL
              </label>
              <input
                type="url"
                id="image_url"
                name="image_url"
                value={formData.image_url || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter image URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="link_url" className="form-label">
                Link URL
              </label>
              <input
                type="url"
                id="link_url"
                name="link_url"
                value={formData.link_url || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter link URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="thumbnail" className="form-label">
                Thumbnail URL
              </label>
              <input
                type="url"
                id="thumbnail"
                name="thumbnail"
                value={formData.thumbnail || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter thumbnail URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="video_url" className="form-label">
                Video URL
              </label>
              <input
                type="url"
                id="video_url"
                name="video_url"
                value={formData.video_url || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter video URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="youtube_url" className="form-label">
                YouTube URL
              </label>
              <input
                type="url"
                id="youtube_url"
                name="youtube_url"
                value={formData.youtube_url || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter YouTube URL"
              />
            </div>

            <div className="form-group">
              <label htmlFor="audio_url" className="form-label">
                Audio URL{formData.post_type === 'audio' ? ' *' : ''}
              </label>
              <input
                type="url"
                id="audio_url"
                name="audio_url"
                value={formData.audio_url || ''}
                onChange={handleInputChange}
                className={`form-input ${errors.audio_url ? 'error' : ''}`}
                placeholder="Enter audio file URL (optional for all post types)"
              />
              {errors.audio_url && (
                <span className="error-message">{errors.audio_url}</span>
              )}
            </div>

            {/* LRC URL - only show when post type is audio or when audio_url is provided */}
            {(formData.post_type === 'audio' || formData.audio_url?.trim()) && (
              <div className="form-group">
                <label htmlFor="lrc_url" className="form-label">
                  LRC URL{formData.post_type === 'audio' ? ' *' : ''}
                </label>
                <input
                  type="url"
                  id="lrc_url"
                  name="lrc_url"
                  value={formData.lrc_url || ''}
                  onChange={handleInputChange}
                  className={`form-input ${errors.lrc_url ? 'error' : ''}`}
                  placeholder="Enter LRC (lyrics) file URL"
                />
                {errors.lrc_url && (
                  <span className="error-message">{errors.lrc_url}</span>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="script" className="form-label">
                Script
              </label>
              <textarea
                id="script"
                name="script"
                value={formData.script || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter script content"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label htmlFor="first_question" className="form-label">
                First Question
              </label>
              <textarea
                id="first_question"
                name="first_question"
                value={formData.first_question || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter first question"
                rows={3}
              />
            </div>

            {/* MCQSC Specific Fields */}
            {formData.post_type === 'mcqsc' && (
              <div className="mcqsc-section">
                <h3 className="section-title">MCQSC Question Details</h3>

                <div className="form-group">
                  <label htmlFor="mcqsc_question" className="form-label">
                    Question <span className="required">*</span>
                  </label>
                  <textarea
                    id="mcqsc_question"
                    name="question"
                    value={mcqscData.question}
                    onChange={handleMcqscInputChange}
                    className={`form-input ${errors.mcqsc_question ? 'error' : ''}`}
                    placeholder="Enter the question"
                    rows={3}
                  />
                  {errors.mcqsc_question && (
                    <span className="error-message">
                      {errors.mcqsc_question}
                    </span>
                  )}
                </div>

                <div className="options-grid">
                  <div className="form-group">
                    <label htmlFor="mcqsc_optionA" className="form-label">
                      Option A <span className="required">*</span>
                    </label>
                    <textarea
                      id="mcqsc_optionA"
                      name="optionA"
                      value={mcqscData.optionA}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionA ? 'error' : ''}`}
                      placeholder="Enter option A"
                      rows={2}
                    />
                    {errors.mcqsc_optionA && (
                      <span className="error-message">
                        {errors.mcqsc_optionA}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="mcqsc_optionB" className="form-label">
                      Option B <span className="required">*</span>
                    </label>
                    <textarea
                      id="mcqsc_optionB"
                      name="optionB"
                      value={mcqscData.optionB}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionB ? 'error' : ''}`}
                      placeholder="Enter option B"
                      rows={2}
                    />
                    {errors.mcqsc_optionB && (
                      <span className="error-message">
                        {errors.mcqsc_optionB}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="mcqsc_optionC" className="form-label">
                      Option C <span className="required">*</span>
                    </label>
                    <textarea
                      id="mcqsc_optionC"
                      name="optionC"
                      value={mcqscData.optionC}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionC ? 'error' : ''}`}
                      placeholder="Enter option C"
                      rows={2}
                    />
                    {errors.mcqsc_optionC && (
                      <span className="error-message">
                        {errors.mcqsc_optionC}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="mcqsc_optionD" className="form-label">
                      Option D <span className="required">*</span>
                    </label>
                    <textarea
                      id="mcqsc_optionD"
                      name="optionD"
                      value={mcqscData.optionD}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionD ? 'error' : ''}`}
                      placeholder="Enter option D"
                      rows={2}
                    />
                    {errors.mcqsc_optionD && (
                      <span className="error-message">
                        {errors.mcqsc_optionD}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="mcqsc_correctAnswer" className="form-label">
                      Correct Answer <span className="required">*</span>
                    </label>
                    <select
                      id="mcqsc_correctAnswer"
                      name="correctAnswer"
                      value={mcqscData.correctAnswer}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_correctAnswer ? 'error' : ''}`}
                    >
                      <option value="">Select correct answer</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                    {errors.mcqsc_correctAnswer && (
                      <span className="error-message">
                        {errors.mcqsc_correctAnswer}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="mcqsc_explanation" className="form-label">
                      Explanation <span className="required">*</span>
                    </label>
                    <textarea
                      id="mcqsc_explanation"
                      name="explanation"
                      value={mcqscData.explanation}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_explanation ? 'error' : ''}`}
                      placeholder="Enter explanation for the correct answer"
                      rows={3}
                    />
                    {errors.mcqsc_explanation && (
                      <span className="error-message">
                        {errors.mcqsc_explanation}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* MCQMC Specific Fields */}
            {formData.post_type === 'mcqmc' && (
              <div className="mcqmc-section">
                <h3 className="section-title">MCQMC Question Details</h3>

                <div className="form-group">
                  <label htmlFor="mcqmc_question" className="form-label">
                    Question <span className="required">*</span>
                  </label>
                  <textarea
                    id="mcqmc_question"
                    name="question"
                    value={mcqmcData.question}
                    onChange={handleMcqmcInputChange}
                    className={`form-input ${
                      errors.mcqmc_question ? 'error' : ''
                    }`}
                    placeholder="Enter the question"
                    rows={3}
                  />
                  {errors.mcqmc_question && (
                    <span className="error-message">
                      {errors.mcqmc_question}
                    </span>
                  )}
                </div>

                <div className="mcqmc-options-container">
                  {['A', 'B', 'C', 'D'].map(option => (
                    <div key={option} className="mcqmc-option-card">
                      <div className="option-header">
                        <label className="checkbox-item">
                          <input
                            type="checkbox"
                            checked={
                              mcqmcData.correctAnswers[
                                option as keyof typeof mcqmcData.correctAnswers
                              ]
                            }
                            onChange={() =>
                              handleMcqmcCorrectAnswerChange(option)
                            }
                            className="checkbox-input"
                          />
                          <span className="checkbox-label">
                            Mark as Correct Answer
                          </span>
                        </label>
                      </div>

                      <div className="form-group">
                        <label
                          htmlFor={`mcqmc_option${option}`}
                          className="form-label"
                        >
                          Option {option} <span className="required">*</span>
                        </label>
                        <textarea
                          id={`mcqmc_option${option}`}
                          name={`option${option}`}
                          value={
                            mcqmcData[
                              `option${option}` as keyof typeof mcqmcData
                            ] as string
                          }
                          onChange={handleMcqmcInputChange}
                          className={`form-input ${
                            errors[`mcqmc_option${option}`] ? 'error' : ''
                          }`}
                          placeholder={`Enter option ${option}`}
                          rows={2}
                        />
                        {errors[`mcqmc_option${option}`] && (
                          <span className="error-message">
                            {errors[`mcqmc_option${option}`]}
                          </span>
                        )}
                      </div>

                      {mcqmcData.correctAnswers[
                        option as keyof typeof mcqmcData.correctAnswers
                      ] && (
                        <div className="form-group">
                          <label
                            htmlFor={`mcqmc_explanation_${option}`}
                            className="form-label"
                          >
                            Explanation for Option {option}{' '}
                            <span className="required">*</span>
                          </label>
                          <textarea
                            id={`mcqmc_explanation_${option}`}
                            value={
                              mcqmcData.explanations[
                                option as keyof typeof mcqmcData.explanations
                              ]
                            }
                            onChange={e =>
                              handleMcqmcExplanationChange(
                                option,
                                e.target.value
                              )
                            }
                            className={`form-input ${
                              errors[`mcqmc_explanation_${option}`]
                                ? 'error'
                                : ''
                            }`}
                            placeholder={`Enter explanation for why option ${option} is correct`}
                            rows={3}
                          />
                          {errors[`mcqmc_explanation_${option}`] && (
                            <span className="error-message">
                              {errors[`mcqmc_explanation_${option}`]}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {Object.values(mcqmcData.correctAnswers).every(val => !val) && (
                  <div className="form-group">
                    {errors.mcqmc_correctAnswers && (
                      <span className="error-message">
                        {errors.mcqmc_correctAnswers}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Poll Specific Fields */}
            {formData.post_type === 'poll' && (
              <div className="mcqsc-section">
                <h3 className="section-title">Poll Question Details</h3>

                <div className="form-group">
                  <label htmlFor="poll_question" className="form-label">
                    Question <span className="required">*</span>
                  </label>
                  <textarea
                    id="poll_question"
                    name="question"
                    value={mcqscData.question}
                    onChange={handleMcqscInputChange}
                    className={`form-input ${errors.mcqsc_question ? 'error' : ''}`}
                    placeholder="Enter the poll question"
                    rows={3}
                  />
                  {errors.mcqsc_question && (
                    <span className="error-message">
                      {errors.mcqsc_question}
                    </span>
                  )}
                </div>

                <div className="options-grid">
                  <div className="form-group">
                    <label htmlFor="poll_optionA" className="form-label">
                      Option A <span className="required">*</span>
                    </label>
                    <textarea
                      id="poll_optionA"
                      name="optionA"
                      value={mcqscData.optionA}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionA ? 'error' : ''}`}
                      placeholder="Enter option A"
                      rows={2}
                    />
                    {errors.mcqsc_optionA && (
                      <span className="error-message">
                        {errors.mcqsc_optionA}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="poll_optionB" className="form-label">
                      Option B <span className="required">*</span>
                    </label>
                    <textarea
                      id="poll_optionB"
                      name="optionB"
                      value={mcqscData.optionB}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionB ? 'error' : ''}`}
                      placeholder="Enter option B"
                      rows={2}
                    />
                    {errors.mcqsc_optionB && (
                      <span className="error-message">
                        {errors.mcqsc_optionB}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="poll_optionC" className="form-label">
                      Option C <span className="required">*</span>
                    </label>
                    <textarea
                      id="poll_optionC"
                      name="optionC"
                      value={mcqscData.optionC}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionC ? 'error' : ''}`}
                      placeholder="Enter option C"
                      rows={2}
                    />
                    {errors.mcqsc_optionC && (
                      <span className="error-message">
                        {errors.mcqsc_optionC}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="poll_optionD" className="form-label">
                      Option D <span className="required">*</span>
                    </label>
                    <textarea
                      id="poll_optionD"
                      name="optionD"
                      value={mcqscData.optionD}
                      onChange={handleMcqscInputChange}
                      className={`form-input ${errors.mcqsc_optionD ? 'error' : ''}`}
                      placeholder="Enter option D"
                      rows={2}
                    />
                    {errors.mcqsc_optionD && (
                      <span className="error-message">
                        {errors.mcqsc_optionD}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* QNA Specific Fields */}
            {formData.post_type === 'qna' && (
              <div className="qna-section">
                <h3 className="section-title">QNA Question Details</h3>

                <div className="form-group">
                  <label htmlFor="qna_question" className="form-label">
                    Question <span className="required">*</span>
                  </label>
                  <textarea
                    id="qna_question"
                    name="question"
                    value={qnaData.question}
                    onChange={handleQnaInputChange}
                    className={`form-input ${
                      errors.qna_question ? 'error' : ''
                    }`}
                    placeholder="Enter the question"
                    rows={3}
                  />
                  {errors.qna_question && (
                    <span className="error-message">{errors.qna_question}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="qna_answer" className="form-label">
                    Answer <span className="required">*</span>
                  </label>
                  <textarea
                    id="qna_answer"
                    name="answer"
                    value={qnaData.answer}
                    onChange={handleQnaInputChange}
                    className={`form-input ${errors.qna_answer ? 'error' : ''}`}
                    placeholder="Enter the correct answer"
                    rows={4}
                  />
                  {errors.qna_answer && (
                    <span className="error-message">{errors.qna_answer}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="qna_explanation" className="form-label">
                    Explanation <span className="required">*</span>
                  </label>
                  <textarea
                    id="qna_explanation"
                    name="explanation"
                    value={qnaData.explanation}
                    onChange={handleQnaInputChange}
                    className={`form-input ${
                      errors.qna_explanation ? 'error' : ''
                    }`}
                    placeholder="Enter explanation for the answer"
                    rows={3}
                  />
                  {errors.qna_explanation && (
                    <span className="error-message">
                      {errors.qna_explanation}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category || ''}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="">Select a category</option>
                {categories.map(slug => (
                  <option key={slug} value={slug}>
                    {categoryMap[slug]}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tags" className="form-label">
                Tags {loadingTags && '(Loading...)'}
              </label>
              <select
                id="tags"
                name="tags"
                multiple
                value={availableTags
                  .filter(tag => formData.tags?.includes(tag.slug))
                  .map(tag => tag.id)}
                onChange={handleTagsChange}
                className="form-select"
                size={5}
              >
                {availableTags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.tag_name}
                  </option>
                ))}
              </select>
              <span className="form-hint">
                Hold Ctrl/Cmd to select multiple tags
              </span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="post_language" className="form-label">
                  Post Language
                </label>
                <select
                  id="post_language"
                  name="post_language"
                  value={formData.post_language || 'hi'}
                  onChange={handleInputChange}
                  className={`form-input ${errors.post_language ? 'error' : ''}`}
                >
                  <option value="en">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
                {errors.post_language && (
                  <span className="error-message">{errors.post_language}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="post_order" className="form-label">
                  Post Order <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="post_order"
                  name="post_order"
                  value={formData.post_order || ''}
                  onChange={handleInputChange}
                  className={`form-input ${errors.post_order ? 'error' : ''}`}
                  placeholder="Enter post order"
                  min="1"
                />
                {errors.post_order && (
                  <span className="error-message">{errors.post_order}</span>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {post ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PostModal;
